import { useCallback, useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { URLSearchParamsInit } from 'react-router-dom';

import { getCurrentWeek, getWeekByOffset } from 'utils/date';

import type { OrganizationJob } from 'state/remote/types';

import useOrganizationJobs from 'state/remote/remote-organization/use-organization-jobs';

import type { OrganizationSearchParams } from './use-organization-planner-search-params';

export type WeekPlannerParams = {
  selectedWeek: [Dayjs, Dayjs];
  switchToWeekByOffset: (offset: number) => void;
  weekList: { weeks: Array<string>; selectedWeekIndex: number };
  setWeek: (startDayOfTheWeek: string) => void;
  error: string | null;
  isLoading: boolean;
  jobs: Array<OrganizationJob>;
};

export const weekPlannerParamsInitialValue: WeekPlannerParams = {
  error: null,
  isLoading: false,
  jobs: [],
  selectedWeek: getCurrentWeek(),
  setWeek: (_) => {},
  switchToWeekByOffset: (_) => {},
  weekList: { selectedWeekIndex: 0, weeks: [] },
};

const useWeekPlannerParams = (
  organizationSearchParams: OrganizationSearchParams,
  plannerSearchParams: {
    searchParams: URLSearchParams;
    setSearchParams: (
      nextInit: URLSearchParamsInit,
      navigateOptions?:
        | {
            replace?: boolean | undefined;
            state?: unknown;
          }
        | undefined,
    ) => void;
    currentSearchParams: Record<string, string>;
  },
): WeekPlannerParams => {
  const currentWeek = useMemo(() => {
    return getCurrentWeek();
  }, []);
  const { currentSearchParams, searchParams, setSearchParams } =
    plannerSearchParams;

  const switchToWeekByOffset = useCallback(
    (offset: number) => {
      const targetWeek = getWeekByOffset(
        [
          dayjs(searchParams.get('fromDate')),
          dayjs(searchParams.get('toDate')),
        ],
        offset,
      );

      setSearchParams({
        ...currentSearchParams,
        fromDate: targetWeek[0].format('YYYY-MM-DD'),
        toDate: targetWeek[1].format('YYYY-MM-DD'),
      });
    },
    [setSearchParams, searchParams, currentSearchParams],
  );

  const { fromDate, toDate } = {
    fromDate: searchParams.get('fromDate'),
    toDate: searchParams.get('toDate'),
  };

  const selectedWeek: [Dayjs, Dayjs] = useMemo(
    () => [dayjs(fromDate), dayjs(toDate)],
    [fromDate, toDate],
  );

  const weekList: { weeks: Array<string>; selectedWeekIndex: number } =
    useMemo(() => {
      const length = 5;
      const weeks: Array<string> = Array(2 * length + 1);
      const format = 'DD/MM/YY';

      for (let i = 0; i < length; i += 1) {
        weeks[i] = getWeekByOffset(selectedWeek, i - length)[0].format(format);
        weeks[length + i + 1] = getWeekByOffset(selectedWeek, i + 1)[0].format(
          format,
        );
      }

      weeks[length] = selectedWeek[0].format(format);

      const result = { selectedWeekIndex: length, weeks };

      return result;
    }, [selectedWeek]);

  const setWeek = useCallback(
    (startDayOfTheWeek: string) => {
      const startDayOfSelectedWeek = dayjs(startDayOfTheWeek, 'DD/MM/YY');

      setSearchParams({
        ...currentSearchParams,
        fromDate: startDayOfSelectedWeek.format('YYYY-MM-DD'),
        toDate: startDayOfSelectedWeek.add(4, 'day').format('YYYY-MM-DD'),
      });
    },
    [setSearchParams, currentSearchParams],
  );

  const {
    data: jobs,
    error,
    isLoading,
  } = useOrganizationJobs({
    assignedStatus: organizationSearchParams.jobStatus,
    fromDate: fromDate ?? currentWeek[0].format('YYYY-MM-DD'),
    jobType: organizationSearchParams.jobType,
    selectedFreelancers: organizationSearchParams.selectedFreelancers,
    selectedLocations: organizationSearchParams.selectedLocations,
    selectedOrganizations: organizationSearchParams.selectedOrganizations,
    toDate: toDate ?? currentWeek[1].format('YYYY-MM-DD'),
  });
  return {
    error,
    isLoading,
    jobs,
    selectedWeek,
    setWeek,
    switchToWeekByOffset,
    weekList,
  };
};

export default useWeekPlannerParams;
